import { useEffect, useRef, useState } from 'react';

export type UseOverflowObserverProps = {
	containerRef: React.MutableRefObject<HTMLDivElement | undefined | null>;
	itemRefs: React.MutableRefObject<React.RefObject<HTMLDivElement | null>[]>;
	itemIds: string[];
	alwaysVisibleRef?: React.MutableRefObject<HTMLDivElement | undefined>;
	firstRenderVisibility?: 'show' | 'hide';
};

export type UseOverflowObserverImplementationProps = UseOverflowObserverProps & {
	enabled: boolean;
};

const INTERSECTION_THRESHOLD = 1;

// Checks whether two ordered item ID lists are identical so observer updates do not trigger
// unnecessary React renders when the set of visible items has not changed.
const areItemIdsEqual = (first: string[], second: string[]): boolean =>
	first.length === second.length && first.every((id, index) => id === second[index]);

// Extracts the item ID from a hidden reference element (for example, "reference:contributor")
// so an IntersectionObserver entry can be associated with the item it is measuring.
const getReferenceElementId = (element: HTMLElement): string | undefined => {
	const targetId = element.getAttribute('id');
	return targetId?.startsWith('reference:') ? targetId.slice('reference:'.length) : undefined;
};

// Reads the layout gap between the dynamic filters and alwaysVisibleRef so the observer reserves
// the same amount of space as the visible row. There is no gap to reserve without a parent.
const getParentColumnGap = (element: HTMLElement): number => {
	if (!element.parentElement) {
		return 0;
	}

	return Number.parseFloat(getComputedStyle(element.parentElement).columnGap || '0') || 0;
};

type OverflowMeasurement = {
	key: string;
	visibleItems: string[];
};

export const useOverflowObserver = ({
	containerRef,
	itemRefs,
	itemIds,
	alwaysVisibleRef,
	firstRenderVisibility = 'show',
	enabled,
}: UseOverflowObserverImplementationProps): string[] => {
	const [measurement, setMeasurement] = useState<OverflowMeasurement>();
	const [alwaysVisibleReservedSpace, setAlwaysVisibleReservedSpace] = useState<number | undefined>(
		alwaysVisibleRef ? undefined : 0,
	);
	// Tracks which items are being shown and the space reserved for alwaysVisibleRef and its gap.
	const measurementKey = JSON.stringify([itemIds, alwaysVisibleReservedSpace ?? 'pending']);
	const itemIdsRef = useRef(itemIds);
	itemIdsRef.current = itemIds;

	// Measures alwaysVisibleRef and its parent gap, keeping the reserved space updated as its
	// contents change.
	// For example, selecting a filter adds the Clear all control and increases the reserved width.
	useEffect(() => {
		if (!enabled) {
			return;
		}

		if (!alwaysVisibleRef) {
			setAlwaysVisibleReservedSpace(0);
			return;
		}

		const element = alwaysVisibleRef.current;
		if (!element) {
			return;
		}

		const updateWidth = () => {
			const nextWidth = element.offsetWidth + getParentColumnGap(element);
			setAlwaysVisibleReservedSpace((currentWidth) =>
				currentWidth === nextWidth ? currentWidth : nextWidth,
			);
		};

		updateWidth();
		const resizeObserver = new ResizeObserver(updateWidth);
		resizeObserver.observe(element);

		return () => {
			resizeObserver.disconnect();
		};
	}, [alwaysVisibleRef, enabled]);

	// Observes every hidden reference item, publishes visibility only after all items have been
	// measured, and keeps the visible list updated as the available layout space changes.
	useEffect(() => {
		if (!enabled || alwaysVisibleReservedSpace === undefined) {
			return;
		}

		let isDisposed = false;
		const intersections = new Map<string, boolean>();
		const observer = new IntersectionObserver(
			(entries) => {
				if (isDisposed) {
					return;
				}

				entries.forEach((entry) => {
					const elementId = getReferenceElementId(entry.target as HTMLElement);
					if (!elementId || !itemIdsRef.current.includes(elementId)) {
						return;
					}

					intersections.set(
						elementId,
						entry.isIntersecting && entry.intersectionRatio >= INTERSECTION_THRESHOLD,
					);
				});

				const currentItemIds = itemIdsRef.current;
				if (!currentItemIds.every((id) => intersections.has(id))) {
					return;
				}

				const nextVisibleItems = currentItemIds.filter((id) => intersections.get(id) === true);

				setMeasurement((previousMeasurement) =>
					previousMeasurement?.key === measurementKey &&
					areItemIdsEqual(previousMeasurement.visibleItems, nextVisibleItems)
						? previousMeasurement
						: { key: measurementKey, visibleItems: nextVisibleItems },
				);
			},
			{
				root: containerRef.current,
				rootMargin: `0px -${alwaysVisibleReservedSpace}px 0px 0px`,
				threshold: INTERSECTION_THRESHOLD,
			},
		);

		itemRefs.current.forEach((itemRef) => {
			if (itemRef?.current) {
				observer.observe(itemRef.current);
			}
		});

		return () => {
			isDisposed = true;
			observer.disconnect();
		};
	}, [containerRef, enabled, itemRefs, measurementKey, alwaysVisibleReservedSpace]);

	const visibleItems = measurement?.visibleItems ?? itemIds;
	const hasMeasured = itemIds.length === 0 || measurement !== undefined;
	return hasMeasured || firstRenderVisibility === 'show' ? visibleItems : [];
};
